import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Combobox,
  ConfirmDialog,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusBadge,
  toast,
} from '@berky/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { UserRound, UsersRound } from 'lucide-react'
import { useEffect, useState } from 'react'

import { findModule } from '../modules'
import {
  usePlatformApi,
  type VerwaltungsGruppe,
  type VerwaltungsRolle,
  type VerzeichnisGruppe,
  type Gruppenmitglied,
} from './api'

/** Wie lange nach dem letzten Tastendruck gewartet wird, bevor gesucht wird. */
const ENTPRELLUNG_MS = 300

/**
 * Gruppen aus dem Verzeichnis mit Rollen verknüpfen.
 *
 * Der Regelfall der Rollenvergabe: Eine Gruppe wird einmal verknüpft, und jedes
 * Mitglied trägt die Rolle — auch die, die morgen dazukommen. Einzelzuweisungen
 * im Reiter „Personen" bleiben die Ausnahme.
 */
export function GruppenTab() {
  const api = usePlatformApi()
  const queryClient = useQueryClient()

  const [eingabe, setEingabe] = useState('')
  const [suche, setSuche] = useState('')
  const [gewaehlteGruppe, setGewaehlteGruppe] = useState<VerzeichnisGruppe | null>(null)
  const [gewaehlteRolle, setGewaehlteRolle] = useState('')
  const [zuLoesen, setZuLoesen] = useState<{
    gruppe: VerwaltungsGruppe
    rolle: string
  } | null>(null)
  const [mitgliederFuer, setMitgliederFuer] = useState<VerwaltungsGruppe | null>(null)

  useEffect(() => {
    const timer = setTimeout(() => setSuche(eingabe), ENTPRELLUNG_MS)
    return () => clearTimeout(timer)
  }, [eingabe])

  const verknuepft = useQuery({
    queryKey: ['verwaltung', 'gruppen'],
    queryFn: ({ signal }) => api.get<VerwaltungsGruppe[]>('/gruppen', { signal }),
  })

  const verzeichnis = useQuery({
    queryKey: ['verwaltung', 'verzeichnis', 'gruppen', suche],
    queryFn: ({ signal }) =>
      api.get<VerzeichnisGruppe[]>('/verzeichnis/gruppen', {
        query: suche ? { suche } : undefined,
        signal,
      }),
    retry: false,
  })

  const rollen = useQuery({
    queryKey: ['verwaltung', 'roles'],
    queryFn: ({ signal }) => api.get<VerwaltungsRolle[]>('/roles', { signal }),
  })

  const mitglieder = useQuery({
    queryKey: ['verwaltung', 'gruppen', mitgliederFuer?.id, 'mitglieder'],
    queryFn: ({ signal }) =>
      api.get<Gruppenmitglied[]>(
        `/gruppen/${encodeURIComponent(mitgliederFuer?.id ?? '')}/mitglieder`,
        { signal },
      ),
    enabled: mitgliederFuer !== null,
  })

  function neuLaden() {
    return queryClient.invalidateQueries({ queryKey: ['verwaltung'] })
  }

  const verknuepfen = useMutation({
    mutationFn: (ziel: { gruppe: VerzeichnisGruppe; rolle: string }) =>
      api.post(`/gruppen/${encodeURIComponent(ziel.gruppe.id)}/rollen`, {
        groupName: ziel.gruppe.name,
        role: ziel.rolle,
      }),
    onSuccess: async () => {
      toast.success('Gruppe verknüpft')
      setGewaehlteGruppe(null)
      setGewaehlteRolle('')
      await neuLaden()
    },
  })

  const loesen = useMutation({
    mutationFn: ({ gruppe, rolle }: { gruppe: VerwaltungsGruppe; rolle: string }) =>
      api.delete(
        `/gruppen/${encodeURIComponent(gruppe.id)}/rollen/${encodeURIComponent(rolle)}`,
      ),
    onSuccess: async () => {
      toast.success('Verknüpfung gelöst')
      setZuLoesen(null)
      await neuLaden()
    },
  })

  const gewaehlteRollenInfo = rollen.data?.find((rolle) => rolle.name === gewaehlteRolle)

  return (
    <div className="space-y-4 pt-4">
      <Alert>
        <UsersRound aria-hidden="true" />
        <AlertTitle>Gruppen sind der einfachste Weg für viele Personen</AlertTitle>
        <AlertDescription>
          Verknüpfen Sie eine M365-Gruppe mit einer Rolle. Aktuelle und zukünftige
          Mitglieder erhalten die Berechtigung automatisch; Einzelzuweisungen bleiben für
          Ausnahmen.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Neue Gruppenzuweisung</CardTitle>
          <CardDescription>
            Wählen Sie zuerst die Gruppe und danach die Rolle. Alle aktuellen und
            zukünftigen Mitglieder erhalten diese Rolle automatisch.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-[1fr_1fr_auto] md:items-end">
            <div>
              <p className="mb-1 text-sm font-medium">1. Gruppe</p>
              <Combobox<VerzeichnisGruppe>
                items={verzeichnis.data ?? []}
                value={gewaehlteGruppe}
                onChange={setGewaehlteGruppe}
                onSearch={setEingabe}
                getKey={(gruppe) => gruppe.id}
                getLabel={(gruppe) => gruppe.name}
                placeholder="Gruppe suchen"
                emptyText="Keine Gruppe gefunden"
                loading={verzeichnis.isFetching}
              />
            </div>

            <div>
              <p className="mb-1 text-sm font-medium">2. Rolle</p>
              <Select value={gewaehlteRolle} onValueChange={setGewaehlteRolle}>
                <SelectTrigger aria-label="Rolle" className="w-full">
                  <SelectValue placeholder="Rolle auswählen" />
                </SelectTrigger>
                <SelectContent>
                  {(rollen.data ?? []).map((rolle) => (
                    <SelectItem key={rolle.name} value={rolle.name}>
                      {rolle.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {gewaehlteRollenInfo && (
                <p className="mt-1 text-xs text-muted-foreground">
                  {`${findModule(gewaehlteRollenInfo.moduleId)?.title ?? gewaehlteRollenInfo.moduleId}: ${gewaehlteRollenInfo.description}`}
                </p>
              )}
            </div>

            <Button
              disabled={!gewaehlteGruppe || !gewaehlteRolle || verknuepfen.isPending}
              onClick={() =>
                gewaehlteGruppe &&
                verknuepfen.mutate({ gruppe: gewaehlteGruppe, rolle: gewaehlteRolle })
              }
            >
              Verknüpfen
            </Button>
          </div>
        </CardContent>
      </Card>

      <DataTable<VerwaltungsGruppe>
        caption="Verknüpfte Gruppen"
        rows={verknuepft.data ?? []}
        rowKey={(gruppe) => gruppe.id}
        isLoading={verknuepft.isPending}
        error={verknuepft.isError ? { onRetry: () => void verknuepft.refetch() } : null}
        emptyState={
          <EmptyState
            title="Noch keine Gruppe verknüpft"
            description="Suchen Sie eine Gruppe aus dem Verzeichnis und wählen Sie eine Rolle."
          />
        }
        columns={[
          {
            key: 'name',
            header: 'Gruppe',
            cell: (gruppe) => <span className="font-medium">{gruppe.name}</span>,
          },
          {
            key: 'rollen',
            header: 'Rollen',
            cell: (gruppe) =>
              gruppe.roles.length === 0 ? (
                <span className="text-sm text-muted-foreground">Keine</span>
              ) : (
                <ul className="space-y-1">
                  {gruppe.roles.map((rolle) => (
                    <li key={rolle} className="flex flex-wrap items-center gap-2">
                      <StatusBadge tone="info">{rolle}</StatusBadge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setZuLoesen({ gruppe, rolle })}
                        aria-label={`${rolle} lösen`}
                      >
                        Lösen
                      </Button>
                    </li>
                  ))}
                </ul>
              ),
          },
          {
            key: 'mitglieder',
            header: 'Mitglieder',
            cell: (gruppe) => (
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <div>
                  <span className="font-medium">{gruppe.memberCount}</span>
                  <span className="block text-xs text-muted-foreground">
                    zuletzt bekannt
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setMitgliederFuer(gruppe)}
                >
                  Mitglieder ansehen
                </Button>
              </div>
            ),
          },
        ]}
      />

      <ConfirmDialog
        open={zuLoesen !== null}
        onOpenChange={(offen) => !offen && setZuLoesen(null)}
        title="Verknüpfung lösen?"
        description={
          zuLoesen
            ? `Mitglieder von „${zuLoesen.gruppe.name}" verlieren damit die Rolle ${zuLoesen.rolle} — es sei denn, sie haben sie direkt zugewiesen bekommen.`
            : ''
        }
        confirmLabel="Lösen"
        destructive
        pending={loesen.isPending}
        onConfirm={() => zuLoesen && loesen.mutate(zuLoesen)}
      />

      <Dialog
        open={mitgliederFuer !== null}
        onOpenChange={(offen) => !offen && setMitgliederFuer(null)}
      >
        {mitgliederFuer && (
          <DialogContent className="max-h-[90svh] overflow-y-auto sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>Mitglieder von {mitgliederFuer.name}</DialogTitle>
              <DialogDescription>
                Diese Liste basiert auf der zuletzt lokal bekannten Gruppenzugehörigkeit.
                Neue Änderungen in M365 können zeitversetzt erscheinen.
              </DialogDescription>
            </DialogHeader>

            {mitglieder.isPending ? (
              <div className="space-y-3">
                <div className="h-12 animate-pulse rounded-lg bg-muted" />
                <div className="h-12 animate-pulse rounded-lg bg-muted" />
              </div>
            ) : mitglieder.data && mitglieder.data.length > 0 ? (
              <ul className="space-y-2">
                {mitglieder.data.map((mitglied) => (
                  <li
                    key={mitglied.userId}
                    className="flex items-center gap-3 rounded-lg border p-3"
                  >
                    <UserRound
                      className="size-5 text-muted-foreground"
                      aria-hidden="true"
                    />
                    <div>
                      <p className="font-medium">{mitglied.name}</p>
                      <p className="text-sm text-muted-foreground">{mitglied.email}</p>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="rounded-lg border p-4 text-sm text-muted-foreground">
                Für diese Gruppe sind aktuell keine Mitglieder bekannt.
              </p>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setMitgliederFuer(null)}>
                Schließen
              </Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>
    </div>
  )
}
